import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { FiLogOut, FiSave } from 'react-icons/fi';

const OperatingHoursAdmin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [schedules, setSchedules] = useState([]);
  const [autoAcceptBookings, setAutoAcceptBookings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchOperatingHours();
  }, [user, navigate]);

  const fetchOperatingHours = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/operating-hours`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Ordina gli schedules per dayOfWeek per assicurare un ordine consistente
      const sortedSchedules = (response.data.operatingHours.schedules || []).sort((a, b) => a.dayOfWeek - b.dayOfWeek);
      setSchedules(sortedSchedules);
      setAutoAcceptBookings(response.data.operatingHours.autoAcceptBookings || false);
      setError(null);
    } catch (err) {
      console.error('Errore caricamento orari:', err);
      setError('Errore nel caricamento degli orari');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const updateSchedule = (dayIndex, field, value) => {
    const newSchedules = [...schedules];
    newSchedules[dayIndex][field] = value;
    setSchedules(newSchedules);
  };

  const toggleNoBreak = (dayIndex) => {
    const newSchedules = [...schedules];
    newSchedules[dayIndex].hasBreak = !newSchedules[dayIndex].hasBreak;
    setSchedules(newSchedules);
  };

  const updateTimeSlot = (dayIndex, field, value) => {
    const newSchedules = [...schedules];
    const slot = newSchedules[dayIndex].timeSlots[0];
    if (slot) {
      slot[field] = value;
      if (field === 'startTime' || field === 'endTime') {
        slot.label = `${slot.startTime} - ${slot.endTime}`;
      }
    }
    setSchedules(newSchedules);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Trasforma i dati: crea UNO SOLO timeSlot per giorno (dall'apertura alla chiusura)
      const cleanedSchedules = schedules.map(day => ({
        dayOfWeek: day.dayOfWeek,
        dayName: day.dayName,
        isOpen: day.isOpen,
        hasBreak: day.hasBreak,
        breakStart: day.breakStart || '12:00',
        breakDuration: day.breakDuration || 60,
        timeSlots: day.isOpen ? [{
          startTime: day.timeSlots?.[0]?.startTime || '08:00',
          endTime: day.timeSlots?.[0]?.endTime || '17:00',
          slotDuration: day.timeSlots?.[0]?.slotDuration || 30,
          capacity: 1,
          label: `${day.timeSlots?.[0]?.startTime || '08:00'} - ${day.timeSlots?.[0]?.endTime || '17:00'}`
        }] : []
      }));
      
      console.log('📤 Saving cleaned schedules:', cleanedSchedules);
      
      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/operating-hours`,
        { 
          schedules: cleanedSchedules,
          autoAcceptBookings
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuccess(true);
      // Ricarica i dati dal backend per verificare il salvataggio
      await fetchOperatingHours();
      setTimeout(() => setSuccess(false), 3000);
      setError(null);
    } catch (err) {
      setError(`Errore nel salvataggio: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-gray-600 text-center py-8">Caricamento orari...</p>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-6 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestione Orari di Apertura</h1>
            <p className="text-purple-100">{user?.companyName} (Admin)</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg transition"
          >
            <FiLogOut size={20} />
            Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-12">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            ✅ Orari salvati con successo!
          </div>
        )}

        {/* Opzioni Generali */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Impostazioni Generali</h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={autoAcceptBookings}
                onChange={(e) => setAutoAcceptBookings(e.target.checked)}
                className="w-5 h-5 cursor-pointer"
              />
              <span className="text-gray-700 font-semibold">
                Auto Accetta Prenotazioni
              </span>
            </label>
            <span className="text-gray-600 text-sm">
              {autoAcceptBookings 
                ? '✓ Le prenotazioni saranno confermate automaticamente' 
                : '○ Le prenotazioni richiederanno conferma manuale'}
            </span>
          </div>
        </div>

        {/* Tabella */}
        <div className="bg-white rounded-lg shadow overflow-x-auto mb-8">
          <table className="w-full text-sm">
            <thead className="bg-purple-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Giorno</th>
                <th className="px-4 py-3 text-center font-semibold">Aperto</th>
                <th className="px-4 py-3 text-left font-semibold">Apertura</th>
                <th className="px-4 py-3 text-left font-semibold">Chiusura</th>
                <th className="px-4 py-3 text-center font-semibold">Nessuna Pausa</th>
                <th className="px-4 py-3 text-left font-semibold">Pausa Inizio</th>
                <th className="px-4 py-3 text-left font-semibold">Durata Pausa (min)</th>
                <th className="px-4 py-3 text-left font-semibold">Durata Slot (min)</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((day, dayIndex) => (
                <tr 
                  key={dayIndex} 
                  className={dayIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className="px-4 py-3 font-bold text-gray-800">{dayNames[dayIndex]}</td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={day.isOpen}
                      onChange={(e) => updateSchedule(dayIndex, 'isOpen', e.target.checked)}
                      className="w-5 h-5 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="time"
                      value={day.timeSlots?.[0]?.startTime || '08:00'}
                      onChange={(e) => updateTimeSlot(dayIndex, 'startTime', e.target.value)}
                      disabled={!day.isOpen}
                      className="px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="time"
                      value={day.timeSlots?.[0]?.endTime || '17:00'}
                      onChange={(e) => updateTimeSlot(dayIndex, 'endTime', e.target.value)}
                      disabled={!day.isOpen}
                      className="px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={!day.hasBreak}
                      onChange={() => toggleNoBreak(dayIndex)}
                      disabled={!day.isOpen}
                      className="w-5 h-5 cursor-pointer disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="time"
                      value={day.breakStart || '12:00'}
                      onChange={(e) => updateSchedule(dayIndex, 'breakStart', e.target.value)}
                      disabled={!day.isOpen || !day.hasBreak}
                      className="px-2 py-1 border border-gray-300 rounded disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={day.breakDuration || 60}
                      onChange={(e) => updateSchedule(dayIndex, 'breakDuration', parseInt(e.target.value))}
                      disabled={!day.isOpen || !day.hasBreak}
                      min="15"
                      max="180"
                      step="15"
                      className="px-2 py-1 border border-gray-300 rounded w-20 disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      value={day.timeSlots?.[0]?.slotDuration || 30}
                      onChange={(e) => updateTimeSlot(dayIndex, 'slotDuration', parseInt(e.target.value))}
                      disabled={!day.isOpen}
                      min="15"
                      max="120"
                      step="15"
                      className="px-2 py-1 border border-gray-300 rounded w-20 disabled:bg-gray-100"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-bold rounded-lg transition"
          >
            <FiSave size={20} />
            {saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </button>
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center gap-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-bold rounded-lg transition"
          >
            Torna alla Dashboard
          </button>
        </div>
      </main>
    </div>
  );
};

export default OperatingHoursAdmin;
